import { create } from 'zustand';

import { ViolationRepository } from '../../api/repository/violation';
import type { ViolationCreatePayload } from '../../api/repository/violation';
import type { Id, QltViolation } from '../../api/types';
import { userError } from '../userError';
import { type MaybeMsg } from '../../i18n/msg.ts';

export interface QltViolationState {
  items: QltViolation[];
  /** The class note the items belong to; a late response for another one is dropped. */
  classNote: Id | null;
  loading: boolean;
  error: MaybeMsg | null;
  loadForClassNote: (classNote: Id) => Promise<QltViolation[]>;
  create: (payload: ViolationCreatePayload) => Promise<QltViolation | null>;
  reset: () => void;
}

export const useQltViolationStore = create<QltViolationState>((set, get) => ({
  items: [],
  classNote: null,
  loading: false,
  error: null,
  async loadForClassNote(classNote) {
    set({ items: get().classNote === classNote ? get().items : [], classNote, loading: true, error: null });
    try {
      const items = await ViolationRepository.fetch<QltViolation[]>({ classNote, _sort: 'createdAt:desc' });
      if (get().classNote !== classNote) return items;
      set({ items, loading: false });
      return items;
    } catch (error) {
      if (get().classNote === classNote) set({ loading: false, error: userError(error) });
      return [];
    }
  },
  async create(payload) {
    set({ loading: true, error: null });
    try {
      const item = await ViolationRepository.create<QltViolation>(payload);
      set({ items: [item].concat(get().items), loading: false });
      return item;
    } catch (error) {
      set({ loading: false, error: userError(error) });
      return null;
    }
  },
  reset: () => set({ items: [], classNote: null, loading: false, error: null }),
}));
